import { $, $$, isTablet, sliderConfig } from '@utils';
import Swiper from 'swiper';
import { Navigation, Scrollbar, FreeMode, A11y } from 'swiper/modules';
import dataLayerPushSlider from '@utils/dataLayerPushSlider';

function init() {
  if (!$('[data-au-comparative]')) return;

  initSliders();
  initMobileLegendIcons();
  observeTargetChanges();

  isTablet.addEventListener('change', () => {
    $$('[data-au-comparative] [data-au-comparative-slider].au-active').forEach((slider) => {
      equalizeRowHeights(slider);
    });
  });
}

function initMobileLegendIcons() {
  $$('[data-au-comparative]').forEach((module) => {
    module.querySelectorAll('[data-au-segmented-control-target]').forEach((slider) => {
      const groupId = slider.dataset.auSegmentedControlTarget;
      const legend = groupId
        ? module.querySelector(`[data-au-comparative-legend="${groupId}"]`)
        : module.querySelector('[data-au-comparative-legend]');
      if (!legend) return;

      legend.setAttribute('aria-hidden', 'true');

      const legendIcons = legend.querySelectorAll('[data-au-comparative-legend-item] [data-au-icon]');
      const firstDetailItem = slider.querySelector('[data-au-comparative-detail-item]');
      const cardsHaveIcons = firstDetailItem && firstDetailItem.querySelector('[data-au-icon]');

      if (cardsHaveIcons) {
        legend.classList.add('au-comparative__legend--has-icons');
      } else {
        slider.querySelectorAll('[data-au-comparative-details]').forEach((card) => {
          card.querySelectorAll('[data-au-comparative-detail-item]').forEach((item, i) => {
            const legendIcon = legendIcons[i];
            if (!legendIcon || item.querySelector('[data-au-comparative-detail-icon-mobile]')) return;
            const clone = legendIcon.cloneNode(true);
            clone.classList.remove('au-comparative__legend-icon');
            clone.classList.add('au-comparative-item__details-icon', 'au-comparative-item__details-icon--mobile');
            clone.setAttribute('data-au-comparative-detail-icon-mobile', '');
            item.insertBefore(clone, item.firstChild);
          });
        });
      }
    });
  });
}

function initSliders() {
  $$('[data-au-comparative] [data-au-comparative-slider].au-active').forEach((slider) => {
    createSwiper(slider);
    syncLegendToSlider(slider);
    equalizeRowHeights(slider);
  });
}

function createSwiper(sliderEl) {
  if (sliderEl._swiper) return;

  const swiper = new Swiper(sliderEl, {
    modules: [Navigation, Scrollbar, FreeMode, A11y],
    ...sliderConfig,
    spaceBetween: 0,
    freeMode: false,
    on: {
      init() {
        updateSliderFades(this);
      },
      slideChange() {
        updateSliderFades(this);
      },

      setTranslate() {
        updateSliderFades(this);
      },
      reachBeginning() {
        updateSliderFades(this);
      },
      reachEnd() {
        updateSliderFades(this);
      },
      fromEdge() {
        updateSliderFades(this);
      },

      resize() {
        updateSliderFades(this);
      },
      update() {
        updateSliderFades(this);
      },
      touchEnd(swiper, event) {
        dataLayerPushSlider(swiper, event);
      },
    },
  });

  updateSliderFades(swiper);

  sliderEl._swiper = swiper;
}

function observeTargetChanges() {
  $$('[data-au-comparative] [data-au-segmented-control-target]').forEach((target) => {
    const observer = new MutationObserver(() => {
      if (!target.classList.contains('au-active')) return;

      if (target.classList.contains('swiper')) {
        createSwiper(target);
      }

      syncLegendToSlider(target);
      equalizeRowHeights(target);
    });

    observer.observe(target, { attributes: true, attributeFilter: ['class'] });
  });
}

function syncLegendToSlider(sliderEl) {
  const module = sliderEl.closest('[data-au-comparative]');
  if (!module) return;

  module.querySelectorAll('[data-au-comparative-legend]').forEach((l) => {
    l.classList.add('au-hide');
    l.setAttribute('aria-hidden', 'true');
  });

  const groupId = sliderEl.dataset.auSegmentedControlTarget;
  const legend = groupId
    ? module.querySelector(`[data-au-comparative-legend="${groupId}"]`)
    : module.querySelector('[data-au-comparative-legend]');
  if (!legend) return;

  // Show this tab's legend; open/closed state depends on whether details are open
  legend.classList.remove('au-hide');
  const firstContent = sliderEl.querySelector('[data-au-comparative-content]');
  const isOpen = !firstContent || !firstContent.classList.contains('au-closed');
  legend.classList.toggle('au-closed', !isOpen);
}

function equalizeRowHeights(sliderEl) {
  const module = sliderEl.closest('[data-au-comparative]');
  if (!module) return;

  const groupId = sliderEl.dataset.auSegmentedControlTarget;
  const legend = groupId
    ? module.querySelector(`[data-au-comparative-legend="${groupId}"]`)
    : module.querySelector('[data-au-comparative-legend]');

  // Reset min-heights on all detail items and legend items
  sliderEl.querySelectorAll('[data-au-comparative-detail-item]').forEach((el) => {
    el.style.minHeight = '';
  });
  if (legend) {
    legend.querySelectorAll('[data-au-comparative-legend-item]').forEach((el) => {
      el.style.minHeight = '';
    });
  }

  const contents = [...sliderEl.querySelectorAll('[data-au-comparative-content]')];
  const wasClosed = contents.map((c) => c.classList.contains('au-closed'));

  // Force reflow
  sliderEl.offsetHeight;

  // Build rows: for each index, collect legend item (if any) + all card detail items
  const detailsByRow = [];
  sliderEl.querySelectorAll('[data-au-comparative-details]').forEach((card) => {
    [...card.querySelectorAll('[data-au-comparative-detail-item]')].forEach((item, i) => {
      if (!detailsByRow[i]) detailsByRow[i] = [];
      detailsByRow[i].push(item);
    });
  });

  detailsByRow.forEach((rowItems, i) => {
    const legendItem = legend?.querySelectorAll('[data-au-comparative-legend-item]')[i];
    const allItems = legendItem ? [legendItem, ...rowItems] : rowItems;
    const maxH = Math.max(...allItems.map((el) => el.offsetHeight));
    allItems.forEach((el) => {
      el.style.minHeight = `${maxH}px`;
    });
  });

  // Restore original open/closed state
  contents.forEach((c, i) => {
    if (wasClosed[i]) c.classList.add('au-closed');
    c.style.transition = '';
  });
}

function updateSliderFades(swiper) {
  const el = swiper.el;
  const controls = el.querySelector('[data-au-slider-controls]');
  const module = el.closest('[data-au-comparative]');

  if (swiper.isLocked) {
    el.classList.remove('au-comparative__slider--fade-left', 'au-comparative__slider--fade-right');
    if (controls) controls.classList.add('au-hide');
    if (module) module.classList.remove('au-comparative--controls-visible');
    updateSlidesInert(swiper);
    return;
  }

  if (controls) controls.classList.remove('au-hide');
  if (module) module.classList.add('au-comparative--controls-visible');
  el.classList.toggle('au-comparative__slider--fade-left', !swiper.isBeginning);
  el.classList.toggle('au-comparative__slider--fade-right', !swiper.isEnd);
  updateSlidesInert(swiper);
}

function updateSlidesInert(swiper) {
  const slides = swiper.slides;
  if (!slides) return;
  const wrapperRect = swiper.wrapperEl.parentElement.getBoundingClientRect();
  slides.forEach((slide) => {
    const rect = slide.getBoundingClientRect();
    const fullyVisible = rect.left >= wrapperRect.left - 1 && rect.right <= wrapperRect.right + 1;
    if (swiper.isLocked || fullyVisible) {
      slide.removeAttribute('inert');
    } else {
      slide.setAttribute('inert', '');
    }
  });
}

export default { init };
